import { useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { contentOptimizerPathes } from '../../constants/queryPathes';
import { statusCodes } from '../../constants/statusCodes';
import { editorTypes } from '../../components/common/tiptapEditor/constants/tiptapEditorConstants';

import {
    updateEditorCurrentVersionScore,
    updateEditorVersionsScores,
} from '../../components/common/tiptapEditor/store/tiptapEditor.actions';
import {
    setContentOptimizerKeywords,
} from '../../components/pages/contentOptimizer/contentPage/store/contentOptimizerContent.actions';
import {
    selectContentOptimizerKeywords,
} from '../../components/pages/contentOptimizer/contentPage/store/contentOptimizerContent.selectors';

import { getAxiosHeaders } from '../helpers/getAxiosHeaders';
import { isCustomKeywordSource } from '../helpers/getSortedOptimizerKeywords';

import { axiosAPI } from '../axiosAPI';

import { useOrganizationSlug } from './useOrganizationSlug';

export const useIgnoreKeyword = () => {
    const dispatch = useDispatch();
    const { contentOptimizerId } = useParams();
    const organizationSlug = useOrganizationSlug();

    const optimizerKeywords = useSelector(selectContentOptimizerKeywords);

    return useCallback(
        (targetKeyword, withBlock) => {
            const keywordsModel = optimizerKeywords.filter((keyword) => {
                if (
                    keyword.keyword.toLocaleLowerCase() !==
                    targetKeyword.keyword.toLocaleLowerCase()
                ) {
                    return true; // if not the target keyword, keep it
                }

                if (isCustomKeywordSource(targetKeyword.source)) {
                    return false; // if the target keyword is a custom keyword, remove it
                }

                if (withBlock) {
                    keyword.removed = withBlock.isBlocked; // toggle removed status while blocking/unblocking
                } else {
                    keyword.removed = !targetKeyword.removed; // toggle removed status while ignoring/restoring
                }

                return true; // keep the target keyword in the optimizer keywords
            });

            return axiosAPI
                .patch(
                    `${contentOptimizerPathes.editContentOptimizer}/${contentOptimizerId}`,
                    {
                        keywords: JSON.stringify(keywordsModel),
                    },
                    {
                        ...getAxiosHeaders(),
                        params: { slug: organizationSlug },
                    }
                )
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        batch(() => {
                            dispatch(
                                setContentOptimizerKeywords(JSON.parse(result?.data?.keywords))
                            );
                            dispatch(
                                updateEditorCurrentVersionScore({
                                    editorType: editorTypes.OPTIMIZER,
                                    score: result?.data?.rankability_score,
                                })
                            );
                            dispatch(
                                updateEditorVersionsScores({
                                    editorType: editorTypes.OPTIMIZER,
                                    scores: result?.data?.versions_scores || [],
                                })
                            );
                        });
                    }

                    return result;
                });
        },
        [dispatch, optimizerKeywords, contentOptimizerId, organizationSlug]
    );
};
